"use client";

import { useState } from "react";
import { CheckIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SiteIcon } from "@/components/site/site-icon";
import { cn } from "@/lib/utils";
import { savePreferredSite } from "@/lib/site/preferred-site-client";
import type { SiteLanguageInfo, SiteLanguageOption } from "@/templates/site/types";

export function siteSwitchTarget(language: SiteLanguageInfo, site: SiteLanguageOption): string {
  return language.switchTargets?.[site.code] ?? site.homeHref;
}

export function SiteLanguageSwitcher({ language }: { language: SiteLanguageInfo }) {
  const t = useTranslations("site.common");
  const [saving, setSaving] = useState(false);

  if (language.sites.length < 2) {
    return null;
  }

  const current = language.sites.find((site) => site.current);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t("language")}
        disabled={saving}
        className="flex size-9 items-center justify-center rounded-lg text-foreground/85 transition-colors hover:bg-foreground/5 hover:text-foreground"
      >
        <SiteIcon src={current?.icon ?? ""} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-auto min-w-40">
        {language.sites.map((site) => (
          <DropdownMenuItem
            key={site.code}
            lang={site.locale}
            className={cn("gap-2.5 px-2 py-1.5", site.current && "bg-accent")}
            disabled={saving}
            onClick={async () => {
              setSaving(true);
              try {
                await savePreferredSite(site.code);
                if (!site.current) window.location.assign(siteSwitchTarget(language, site));
                else toast.success(t("languageSaved"));
              } catch (error) {
                toast.error(error instanceof Error ? error.message : t("networkError"));
              } finally {
                setSaving(false);
              }
            }}
          >
            <SiteIcon src={site.icon} />
            <span className="flex-1">{site.name}</span>
            {site.current ? <CheckIcon className="size-4 text-brand" aria-hidden /> : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
